import React, { useState } from 'react';
/** Components */
import { Button, TextField, Dialog, DialogTitle, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import { LoadingButton } from '@mui/lab';

/** Types, Utils */
import { validateEmail } from '@utils/validate';
import { useStyles } from '../styles/ForgotPassDialog.styles';
import { authApi, UserUrls } from '@api/user';
import { useAlert } from '@hooks/useAlert';

interface ForgotPassDialogProps {
    open: boolean;
    onClose: () => void;
}

export const ForgotPassDialog = ({ open, onClose }: ForgotPassDialogProps) => {
    const classes = useStyles();

    const [email, setEmail] = useState('');
    const [error, setError] = useState<string | undefined>('');

    const { setAlert } = useAlert();

    const updateEmail = (email: string) => {
        const { message } = validateEmail(email);

        setError(message);
        setEmail(email);
    };

    const onReset = async () => {
        // ? axios can return a hard error if its a 400 or 500, but not 300's. Anything other than 200 should be considered an error
        const resp = await authApi.get(UserUrls.ResetPass, { params: { email } }).catch(() => {
            setAlert('Could not send reset link, contact customer service if the issue persists.', 'error');
        });
        if (resp && resp.status === 200) {
            setAlert('Successfully sent reset link, check your inbox.', 'success');
        } else {
            setAlert('Could not send reset link, contact customer service if the issue persists.', 'error');
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle className={classes.dialogTitle}>Reset Password</DialogTitle>

            <DialogContent style={{ marginTop: 20, paddingBottom: 10 }}>
                <DialogContentText style={{ marginBottom: 10 }} color='textPrimary'>
                    Enter your email and we will send you a link to reset password.
                </DialogContentText>

                <TextField
                    error={Boolean(error)}
                    helperText={error ? error : ''}
                    size='small'
                    fullWidth
                    placeholder='Email address'
                    name='email'
                    type='email'
                    variant='outlined'
                    onChange={(e) => updateEmail(e.target.value)}
                    value={email}
                />
            </DialogContent>

            <DialogActions style={{ margin: '0px 16px 16px 16px' }}>
                <Button
                    variant='contained'
                    disableElevation
                    onClick={() => {
                        onClose();
                        setEmail('');
                    }}
                    autoFocus>
                    Close
                </Button>
                <LoadingButton
                    loading={false}
                    disabled={Boolean(error) || !email}
                    variant='contained'
                    disableElevation
                    onClick={onReset}>
                    Reset
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
